import { Grid, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import React, { useEffect, useState, useCallback, useRef } from "react";
import Helmet from "react-helmet";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import PageviewIcon from "@mui/icons-material/Pageview";
import {
    Card,
    CardContent,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { PagedTable, pageStore } from "../../common";
import { Portlet, PortletBody } from "../../partials/content/Portlet";
import {
    claimAnalysisKnowledgeBaseLanding,
    resetPage,
    set,
    setMulti,
} from "../../store/actions/pages";
import { RootState } from "../../store/reducers";
import { useKnowledgeBaseFields } from "./index";
import * as SUPPLEMENTARY_KNOWLEDGE_API from "../../store/api/supplementaryKnowledge";

const { usePageStore, useGridFilters } = pageStore(
    "iq-knowledge-base-landing",
    {}
);

export default function ClaimAnalysisKnowledgeBasePage() {
    const [refresh, setRefresh] = useState(Promise.resolve());
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [documentsWithStatus, setDocumentsWithStatus] = useState([]);
    const [isPolling, setIsPolling] = useState(false);
    const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);

    const pollingIntervalRef = useRef<any>(null);
    const history = useHistory();
    const filters: any = useSelector<RootState>(
        (state) => state?.pages?.["iq-knowledge-base-landing"]
    );
    const appliedFilters = filters?.filter;
    const dispatch = useDispatch();

    const knowledgeBaseData = usePageStore((s) => {
        return s.list;
    });

    const pending = usePageStore((s) => s.pending);

    const fields = useKnowledgeBaseFields();

    const tableFilter = useGridFilters();

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }

    useEffect(() => {
        const pageName = { name: "iq-knowledge-base-landing" };
        // Initialize waitForCriteriaFilter - the reducer will merge with initial state if needed
        dispatch(set(pageName.name, "waitForCriteriaFilter", false));
        return () => {
            dispatch(resetPage(pageName.name));
        };
    }, [dispatch]);

    const handleDeleteClick = useCallback((row: any) => {
        setSelectedDocument(row);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!selectedDocument) return;

        setIsDeleting(true);
        try {
            // First delete the document from database
            await SUPPLEMENTARY_KNOWLEDGE_API.deleteSuppKbDocument(selectedDocument.id);
            
            // Then delete from AI backend if documentMetadataId exists
            if (selectedDocument.documentMetadataId) {
                await SUPPLEMENTARY_KNOWLEDGE_API.deleteSuppKbDocumentFromAI(selectedDocument.documentMetadataId);
            }
            
            toast.success("Document deleted successfully",{
                autoClose: 5000,
            });
            setDeleteDialogOpen(false);
            setSelectedDocument(null);
            
            // Remove deleted document from local state to update table immediately
            setDocumentsWithStatus(prev => prev.filter((doc: any) => doc.id !== selectedDocument.id));
            
            // Refresh the table
            setRefresh(Promise.resolve());
        } catch (error: any) {
            console.error("Error deleting document:", error);
            toast.error(error?.message || "Failed to delete document",{
                autoClose: 5000,
            });
        } finally {
            setIsDeleting(false);
        }
    }, [selectedDocument]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setSelectedDocument(null);
    }, []);

    // Fetch and merge document status
    const fetchDocumentStatus = useCallback(async () => {
        try {
            setIsFetchingStatuses(true);
            const statusData = await SUPPLEMENTARY_KNOWLEDGE_API.getSuppKbDocumentStatus();
            
            // Merge status with document list
            const merged = knowledgeBaseData.map(doc => {
                const status = statusData.find(
                    (s: any) => s.document_metadata_id === doc.documentMetadataId
                );
                return {
                    ...doc,
                    processing_status: status?.processing_status || 'Not Started',
                    vector_store_ids: status?.vector_store_ids || [],
                    contractNames: doc.suppKBDocumentMetadataContractMap?.map((item: any) => item.contract?.name).filter(Boolean) || []
                };
            });
            
            setDocumentsWithStatus(merged);
            
            // Check if we should continue polling
            // Continue polling if ANY document is in a non-terminal state:
            // - Not Started, Queued, In Process
            // Stop polling only when ALL documents are in terminal states:
            // - Processed, Failed
            const hasProcessing = merged.some(
                (doc: any) => {
                    const status = String(doc.processing_status).toLowerCase();
                    return ['not started', 'queued', 'pending', 'in process'].includes(status);
                }
            );
            setIsFetchingStatuses(false);
            return hasProcessing;
        } catch (error) {
            setIsFetchingStatuses(false);
            console.error('Error fetching document status:', error);
            return false;
        }
    }, [knowledgeBaseData]);

    // Start polling for status updates
    useEffect(() => {
        if (knowledgeBaseData?.length > 0) {
            // Initialize with default status first
            const initialData = knowledgeBaseData.map(doc => {
                const contractNames = doc.suppKBDocumentMetadataContractMap?.map((item: any) => item.contract?.name).filter(Boolean) || [];
                return {
                    ...doc,
                    processing_status: doc.processing_status || 'Not Started',
                    vector_store_ids: doc.vector_store_ids || [],
                    contractNames
                };
            });
            setDocumentsWithStatus(initialData);
            
            // Then fetch actual status
            fetchDocumentStatus().then(shouldContinue => {
                // Clear any existing polling interval
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                
                if (shouldContinue) {
                    setIsPolling(true);
                    pollingIntervalRef.current = setInterval(async () => {
                        const continuePolling = await fetchDocumentStatus();
                        if (!continuePolling) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                            setIsPolling(false);
                        }
                    }, 5000); // Poll every 5 seconds
                } else {
                    setIsPolling(false);
                }
            });
        }
        
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [knowledgeBaseData]);

    // Retry handler for failed documents
    const handleRetry = useCallback(async (row: any) => {
        try {
            // Get first contract ID from the document's contract map
            const contractId = row.suppKBDocumentMetadataContractMap?.[0]?.contractId;
            if (!contractId) {
                toast.error('No contract associated with this document',{
                    autoClose: 5000,
                });
                return;
            }
            
            await SUPPLEMENTARY_KNOWLEDGE_API.retrySuppKbDocument(contractId, row.documentMetadata);
            toast.success('Document retry initiated',{
                autoClose: 5000,
            });
            
            // Start polling again
            setIsPolling(true);
            fetchDocumentStatus();
        } catch (error) {
            console.error('Error retrying document:', error);
            toast.error('Failed to retry document',{
                autoClose: 5000,
            });
        }
    }, [fetchDocumentStatus]);

    const actionComponent = useCallback((row: any) => {
        const isFailed = row.processing_status === 'Failed';
        
        return (
            <>
                {isFailed && (
                    <Tooltip title="Retry">
                        <IconButton
                            size="small"
                            onClick={() => handleRetry(row)}
                            color="warning"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="View">
                    <IconButton
                        size="small"
                        onClick={() => {
                            history.push(`/cms/iq-knowledge-base/view/${row.id}`);
                        }}
                        color="primary"
                    >
                        <PageviewIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                    <IconButton
                        size="small"
                        onClick={() => {
                            history.push(`/cms/iq-knowledge-base/edit/${row.id}`);
                        }}
                        color="primary"
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(row)}
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </>
        );
    }, [handleDeleteClick, handleRetry, history]);

    const handleLoad = useCallback((sort: any, filter: any, pagination: any) => {
        dispatch(
            claimAnalysisKnowledgeBaseLanding(
                pagination,
                sort,
                filter
            )
        );
    }, [dispatch]);

    const handleAdd = useCallback(() => {
        history.push('/cms/iq-knowledge-base/edit/new');
    }, [history]);

    return (
        <>
            <Helmet title="IQ Knowledge Base" />
            <Portlet>
                <PortletBody>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <PagedTable
                                        name="iq-knowledge-base-landing"
                                        itemName="Supplementary Knowledge Document"
                                        refresh={refresh}
                                        loading={pending || isFetchingStatuses}
                                        data={isFetchingStatuses ? []:documentsWithStatus}
                                        fields={fields}
                                        hasActions={true}
                                        renderActionColumn={actionComponent}
                                        showFilterColumnsDialog={false}
                                        criteriasSaveEnabled={false}
                                        paginationFilters={tableFilter}
                                        defaultSort={[
                                            { id: "id", direction: "desc" },
                                        ]}
                                        onLoad={handleLoad}
                                        filterData={appliedFilters}
                                        onAdd={handleAdd}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </PortletBody>
            </Portlet>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the document "{selectedDocument?.documentMetadata?.documentName}"? 
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleDeleteCancel} 
                        color="primary"
                        disabled={isDeleting}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="primary" 
                        variant="contained"
                        disabled={isDeleting}
                        autoFocus
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
